<template>
  <div
    v-if="showModal"
    class="modal-overlay"
  >
    <div class="modal-content">
      <h2>Start Your Journey</h2>
      <p>Welcome to <strong>{{ programTitle }}</strong>. Please select your start date to begin.</p>

      <div
        v-if="loading"
        class="loading"
      >
        Loading upcoming dates...
      </div>

      <div
        v-else
        class="wave-list"
      >
        <!-- Go at your own pace option -->
        <div
          class="wave-option"
          :class="{ selected: selectedWaveId === 'own_pace' }"
          @click="selectedWaveId = 'own_pace'"
        >
          <div class="wave-header">
            <span class="wave-title">Go at your own pace</span>
            <span class="wave-date">Start immediately</span>
          </div>
          <p class="wave-desc">
            Begin the program right away and work through it at your own speed.
          </p>
        </div>

        <!-- Wave options -->
        <div
          v-for="wave in waves"
          :key="wave.id"
          class="wave-option"
          :class="{ selected: selectedWaveId === wave.id }"
          @click="selectedWaveId = wave.id"
        >
          <div class="wave-header">
            <span class="wave-title">{{ wave.title }}</span>
            <span class="wave-date">Starts: {{ formatDate(wave.start_date) }}</span>
          </div>
          <p class="wave-desc">
            {{ wave.description }}
          </p>
        </div>
      </div>

      <button
        :disabled="selectedWaveId === null || submitting"
        class="btn-primary"
        @click="confirmWave"
      >
        {{ submitting ? 'Confirming...' : selectedWaveId === 'own_pace' ? 'Start Now' : 'Join This Wave' }}
      </button>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api';

export default {
  name: 'ChooseWaveModal',
  props: {
    enrollmentId: {
      type: Number,
      required: true,
    },
    programTitle: {
      type: String,
      default: 'your program',
    },
  },
  data() {
    return {
      waves: [],
      selectedWaveId: null,
      loading: true,
      submitting: false,
      showModal: true,
    };
  },
  computed: {
    userData() {
      return this.$store.state.userData;
    },
  },
  mounted() {
    this.fetchWaves();
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';

      const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
      let date;

      if (dateOnlyPattern.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateStr);
      }

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    selectWave(id) {
      this.selectedWaveId = id;
    },
    async fetchWaves() {
      this.loading = true;
      this.errorMessage = null;

      try {
        const requestConfig = {
          url: `enrollments/${this.enrollmentId}/available_waves/`,
          method: 'GET',
        };

        if (this.userData && this.userData.drf_token) {
          requestConfig.headers = {
            Authorization: `Token ${this.userData.drf_token}`,
          };
        }

        const response = await api(requestConfig);

        if (response.ok) {
          this.waves = response.body;
        } else {
          this.errorMessage = 'Failed to load upcoming dates.';
        }
      } catch (e) {
        this.errorMessage = 'Connection error. Please try refreshing.';
      } finally {
        this.loading = false;
      }
    },
    async confirmWave() {
      if (this.selectedWaveId === null) return;

      this.submitting = true;
      this.errorMessage = null;

      try {
        // Prepare the request body - send null explicitly for "own_pace"
        const requestBody = {};
        if (this.selectedWaveId === 'own_pace') {
          requestBody.wave_id = null;
        } else {
          requestBody.wave_id = this.selectedWaveId;
        }

        const requestConfig = {
          url: `enrollments/${this.enrollmentId}/select_wave/`,
          method: 'POST',
          body: JSON.stringify(requestBody),
          headers: {
            'Content-Type': 'application/json',
          },
        };

        if (this.userData && this.userData.drf_token) {
          requestConfig.headers.Authorization = `Token ${this.userData.drf_token}`;
        }

        const response = await api(requestConfig);

        if (response.ok) {
          // Emit event to parent first - parent will close via v-if
          this.$emit('wave-selected', response.body);
          // Also set local showModal to false as backup
          this.$nextTick(() => {
            this.showModal = false;
          });
        } else {
          const errors = response.body;
          if (errors && errors.non_field_errors) {
            [this.errorMessage] = errors.non_field_errors;
          } else {
            this.errorMessage = 'Could not complete your selection. Please try again.';
          }
        }
      } catch (e) {
        this.errorMessage = 'An unexpected error occurred.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 2px 10px rgba(0,0,0,0.3);
}

.wave-list {
  max-height: 300px;
  overflow-y: auto;
  margin: 20px 0;
}

.wave-option {
  border: 1px solid #ddd;
  padding: 15px;
  margin-bottom: 10px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.wave-option:hover {
  background-color: #f9f9f9;
}

.wave-option.selected {
  border-color: #007bff;
  background-color: #e7f1ff;
  box-shadow: 0 0 0 1px #007bff;
}

.wave-header {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  margin-bottom: 5px;
}

.wave-desc {
  font-size: 0.9em;
  color: #666;
  margin: 0;
}

.loading {
  text-align: center;
  padding: 20px;
  color: #666;
}

button:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}
</style>
